/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useState } from "react";
import { useApp } from "../../../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { toast } from "../../../components/ui/sonner";
import { Loader2 } from "lucide-react";
import { inp } from "../hrmsShared";
import { L, fmtY, errMsg } from "./cnShared";

export const CnRunDialog = ({ row, items, zh, onClose, onSaved }) => {
  const { api } = useApp();
  const T = (z, e) => (zh ? z : e);
  const [f, setF] = useState({ ...Object.fromEntries(items.map((it) => [it[0], row[it[0]] || ""])), note: row.note || "", bonusSeparate: !!row.bonusSeparate });
  const [preview, setPreview] = useState(row);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async (close) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/hrms/cn-payroll/runs/${row.runId}`, f);
      setPreview(data);
      toast.success(T("已保存", "Saved"));
      if (close) onSaved(data);
    } catch (e) { toast.error(errMsg(e, T("保存失败", "Save failed"))); }
    setBusy(false);
  };
  const lab = "block text-xs text-slate-500";
  const kv = (l, v, c = "text-slate-700", tid) => <div className="flex-wrap md:flex-nowrap flex justify-between text-xs py-0.5" data-testid={tid}><span className="text-slate-500">{l}</span><b className={c}>{fmtY(v)}</b></div>;
  const field = (it) => (
    <label key={it[0]} className={lab}>{L(it, zh)}{it[0] === "tax" && <span className="ml-1 text-[10px] text-amber-600">{T("HR 按申报数手填", "manual, per filing")}</span>}
      <input data-testid={`hrms-cn-run-in-${it[0]}`} type="number" step="0.01" className={inp} value={f[it[0]]} onChange={(e) => set(it[0], e.target.value)} placeholder="0" disabled={it[0] === "bonusTax" && !f.bonusSeparate} />
    </label>);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{T("月度录入", "Monthly entry")} · {row.name} · {row.month || ""}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 text-sm" data-testid="hrms-cn-run-dialog">
          <div className="space-y-2.5">
            {items.filter((it) => it[0] !== "bonusTax").map(field)}
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5 space-y-2">
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 font-semibold">
                <input data-testid="hrms-cn-run-in-bonusSeparate" type="checkbox" checked={f.bonusSeparate} onChange={(e) => set("bonusSeparate", e.target.checked)} />
                {T("年终奖单独计税（全年一次性奖金）", "Annual bonus taxed separately")}
              </label>
              <p className="text-[10px] text-slate-500">{T("勾选后「奖金」按年终奖单独计税，并在工资单单列年终奖个税，不并入工资薪金个税。", "When checked, the bonus is treated as a one-off annual bonus with its own IIT line on the payslip.")}</p>
              {items.filter((it) => it[0] === "bonusTax").map(field)}
            </div>
            <label className={lab}>{T("备注", "Note")}<input data-testid="hrms-cn-run-in-note" className={inp} value={f.note} onChange={(e) => set("note", e.target.value)} /></label>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 self-start" data-testid="hrms-cn-run-preview">
            <p className="text-[11px] font-bold text-slate-500 mb-1">{T("核算预览（点「保存并预览」刷新）", "Preview (click Save & preview to refresh)")}</p>
            {kv(T("固定工资", "Fixed"), preview.fixedTotal)}
            {kv(T("应发合计", "Gross"), preview.gross, "text-slate-900")}
            {kv(T("社保个人", "SI employee"), preview.siEe, "text-red-600")}
            {kv(T("公积金个人", "HF employee"), preview.hfEe, "text-red-600")}
            {kv(T("个税（工资薪金）", "IIT (wages)"), preview.tax, "text-red-600")}
            {preview.bonusSeparate && kv(T("年终奖个税（单独计税）", "IIT (annual bonus)"), preview.bonusTax, "text-red-600", "hrms-cn-run-preview-bonusTax")}
            {kv(T("其他扣款", "Other ded."), preview.adjustMinus, "text-red-600")}
            <div className="border-t border-slate-200 my-1" />
            {kv(T("实发工资", "Net pay"), preview.net, "text-emerald-700 text-sm", "hrms-cn-run-preview-net")}
            <div className="border-t border-slate-200 my-1" />
            {kv(T("社保单位", "SI employer"), preview.siEr, "text-slate-500")}
            {kv(T("公积金单位", "HF employer"), preview.hfEr, "text-slate-500")}
            {kv(T("单位用工成本", "Employer cost"), preview.employerCost, "text-slate-800")}
          </div>
        </div>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("关闭", "Close")}</button>
          <button data-testid="hrms-cn-run-preview-btn" disabled={busy} onClick={() => save(false)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full border border-slate-300 text-slate-700 font-semibold px-4 py-2 disabled:opacity-50">{T("保存并预览", "Save & preview")}</button>
          <button data-testid="hrms-cn-run-save" disabled={busy} onClick={() => save(true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存并关闭", "Save & close")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
